import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";

import api, { formatApiError } from "../../lib/api";
import {
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextInput,
  TextArea,
  ErrorBanner,
  INPUT_CLS,
} from "../crm/Form";
import { ALERT_TYPES, SEVERITIES, ingestAlert } from "../../lib/monitoring";

export default function LogAlertModal({ open, onClose, onCreated }) {
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerOpts, setCustomerOpts] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [systems, setSystems] = useState([]);
  const [systemId, setSystemId] = useState("");
  const [alertType, setAlertType] = useState("system_offline");
  const [severity, setSeverity] = useState("medium");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => {
    setCustomerSearch("");
    setCustomerOpts([]);
    setCustomer(null);
    setSystems([]);
    setSystemId("");
    setAlertType("system_offline");
    setSeverity("medium");
    setTitle("");
    setDescription("");
    setErr("");
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose && onClose();
  };

  // Debounce customer search.
  useEffect(() => {
    if (!customerSearch.trim() || customer) {
      setCustomerOpts([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/customers/", {
          params: { q: customerSearch.trim(), limit: 8 },
        });
        setCustomerOpts(r.data || []);
      } catch {
        setCustomerOpts([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [customerSearch, customer]);

  const pickCustomer = async (c) => {
    setCustomer(c);
    setCustomerSearch(c.name);
    setCustomerOpts([]);
    // Fetch full customer (which includes solar_systems).
    try {
      const full = await api.get(`/customers/${c.id}`);
      setSystems(full.data?.solar_systems || []);
    } catch {
      setSystems([]);
    }
  };

  const submit = async () => {
    setErr("");
    if (!title.trim()) {
      setErr("Title is required.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        vendor: "manual",
        vendor_alert_id: null,
        system_id: systemId || null,
        alert_type: alertType,
        severity,
        title: title.trim(),
        description: description.trim() || null,
        raw_payload: null,
      };
      const data = await ingestAlert(body);
      toast.success("Alert logged");
      reset();
      onCreated && onCreated(data.alert);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={close} title="Log alert" subtitle="manual" data-testid="log-alert-modal">
      <div className="space-y-4">
        <ErrorBanner message={err} />
        <Field label="Customer" hint="Type to search by name">
          <div className="relative">
            <div className="flex items-center gap-2">
              <MagnifyingGlassIcon size={14} className="absolute left-3 text-slate-400" />
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  if (customer) {
                    setCustomer(null);
                    setSystems([]);
                    setSystemId("");
                  }
                }}
                placeholder="Search customer…"
                data-testid="log-alert-customer-search"
                className={`${INPUT_CLS} pl-8`}
              />
            </div>
            {customerSearch.trim() && customerOpts.length > 0 && !customer && (
              <div
                className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-10 max-h-56 overflow-auto"
                data-testid="log-alert-customer-options"
              >
                {customerOpts.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => pickCustomer(c)}
                    data-testid={`log-alert-customer-opt-${c.id}`}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                  >
                    <div className="font-semibold text-slate-900">{c.name}</div>
                    {c.email && <div className="text-xs text-slate-500">{c.email}</div>}
                  </button>
                ))}
              </div>
            )}
            {searching && customerSearch.trim() && (
              <div className="text-[10px] text-slate-400 mt-1">Searching…</div>
            )}
          </div>
        </Field>

        {customer && (
          <Field
            label="Solar system"
            hint={
              systems.length === 0
                ? "Customer has no systems on file — alert will not be linked"
                : "Required to denormalize customer + property on the alert"
            }
          >
            <Select
              value={systemId}
              onChange={(v) => setSystemId(v || "")}
              options={[
                { value: "", label: "— No system / unlinked —" },
                ...systems.map((s) => ({
                  value: s.id,
                  label: s.label || s.name || s.id,
                })),
              ]}
              placeholder=""
              data-testid="log-alert-system"
            />
          </Field>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Alert type" required>
            <Select
              value={alertType}
              onChange={(v) => setAlertType(v || "system_offline")}
              options={ALERT_TYPES}
              placeholder=""
              data-testid="log-alert-type"
            />
          </Field>
          <Field label="Severity" required>
            <Select
              value={severity}
              onChange={(v) => setSeverity(v || "medium")}
              options={SEVERITIES}
              placeholder=""
              data-testid="log-alert-severity"
            />
          </Field>
        </div>

        <Field label="Title" required>
          <TextInput
            value={title}
            onChange={setTitle}
            maxLength={120}
            placeholder="e.g. Inverter offline since 8:30am"
            data-testid="log-alert-title"
          />
        </Field>

        <Field label="Description" hint="What did the tech see / what does the vendor portal show?">
          <TextArea
            value={description}
            onChange={setDescription}
            rows={3}
            maxLength={2000}
            data-testid="log-alert-description"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={close} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={submit} disabled={busy} data-testid="log-alert-submit">
            {busy ? "Logging…" : "Log alert"}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}
